import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { api, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Trophy, Medal, Award, Crown } from "lucide-react";

const PODIUM = [
  { icon: Crown, cls: "text-amber-500", ring: "ring-amber-400" },
  { icon: Medal, cls: "text-slate-400", ring: "ring-slate-300" },
  { icon: Award, cls: "text-orange-600", ring: "ring-orange-400" },
];

export default function Leaderboard() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/productivity/leaderboard").then((r) => setData(r.data)).catch(() => {}); }, []);

  if (!data) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-72" /></div>;
  const leaders = data.leaders;
  const initials = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

  return (
    <div className="space-y-6">
      <div>
        <p className="uppercase text-[10px] tracking-wider font-semibold text-muted-foreground">Semangat Tim</p>
        <h1 className="font-heading text-3xl font-bold tracking-tight flex items-center gap-2"><Trophy className="h-7 w-7 text-amber-500" /> Papan Peringkat</h1>
        <p className="text-sm text-muted-foreground mt-1">Arsip disetujui minggu ini (sejak {data.week_start}).</p>
      </div>

      {leaders.length === 0 ? (
        <Card className="p-16 text-center"><Trophy className="h-10 w-10 mx-auto text-muted-foreground" /><p className="mt-3 text-muted-foreground">Belum ada arsip disetujui minggu ini. Jadilah yang pertama! 🚀</p></Card>
      ) : (
        <>
          <div className="grid sm:grid-cols-3 gap-4">
            {leaders.slice(0, 3).map((l, i) => {
              const P = PODIUM[i];
              return (
                <motion.div key={l.user_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.08 }}
                  className={i === 0 ? "sm:order-2" : i === 1 ? "sm:order-1" : "sm:order-3"}>
                  <Card className={`p-5 text-center ${i === 0 ? "border-amber-400/50 shadow-md" : ""}`}>
                    <div className="flex justify-center mb-2"><P.icon className={`h-6 w-6 ${P.cls}`} /></div>
                    <Avatar className={`h-16 w-16 mx-auto ring-2 ${P.ring}`}><AvatarFallback className="bg-primary text-primary-foreground font-semibold">{initials(l.name)}</AvatarFallback></Avatar>
                    <p className="font-heading font-semibold mt-3">{l.name}{l.user_id === data.me && <span className="text-primary"> (Anda)</span>}</p>
                    <p className="text-[11px] text-muted-foreground">{ROLE_LABELS[l.role] || l.role}</p>
                    <p className="font-heading text-3xl font-bold mt-2 tabular-nums">{l.count}</p>
                    <p className="text-xs text-muted-foreground">arsip disetujui ≈ {l.mL} mL</p>
                  </Card>
                </motion.div>
              );
            })}
          </div>

          {leaders.length > 3 && (
            <Card className="overflow-hidden">
              <div className="divide-y divide-border">
                {leaders.slice(3).map((l, i) => (
                  <motion.div key={l.user_id} initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.03 }}
                    className={`flex items-center gap-4 px-4 py-3 ${l.user_id === data.me ? "bg-accent/50" : ""}`}>
                    <span className="font-heading font-bold text-lg tabular-nums w-8 text-muted-foreground">{l.rank}</span>
                    <Avatar className="h-9 w-9"><AvatarFallback className="bg-secondary text-secondary-foreground text-xs font-semibold">{initials(l.name)}</AvatarFallback></Avatar>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm truncate">{l.name}{l.user_id === data.me && <span className="text-primary"> (Anda)</span>}</p>
                      <p className="text-[11px] text-muted-foreground">{ROLE_LABELS[l.role] || l.role}</p>
                    </div>
                    <Badge variant="secondary" className="tabular-nums">{l.count} arsip ≈ {l.mL} mL</Badge>
                  </motion.div>
                ))}
              </div>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
